'use client'

import * as React from 'react'
import Image from 'next/image'
import '@/app/(app)/vector/vector-page.css'
import {
  DollarSign,
  Clock,
  Target,
  MessageCircle,
  BookOpen,
  Crown,
  AlertCircle,
  CheckCircle,
  Heart,
  Database,
  Zap,
  Network,
  Grid3x3,
  Bot,
} from 'lucide-react'

const styles = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes orbitClockwise {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes orbitCounterClockwise {
    from { transform: rotate(0deg); }
    to { transform: rotate(-360deg); }
  }

  .counter-rotate-node {
    animation: orbitCounterClockwise 60s linear infinite;
    transform-origin: center center;
    will-change: transform;
  }

  /* Gradient Mask: Visible top, fades out bottom */
  .arc-mask-gradient {
    mask-image: linear-gradient(to bottom, black 0%, black 40%, transparent 80%);
    -webkit-mask-image: linear-gradient(to bottom, black 0%, black 40%, transparent 80%);
  }
`

const integrations = [
  {
    label: 'Intent',
    category: 'Direction',
    icon: Target,
    color: 'purple',
    description:
      'Analyzes conversation patterns to identify high-intent buyers vs. casual researchers. 47% higher close rates on identified prospects.',
  },
  {
    label: 'Budget',
    category: 'Financial',
    icon: DollarSign,
    color: 'emerald',
    description:
      'Extracts budget constraints through natural conversation—not forms. 85% completion vs. 40% traditional form fills.',
  },
  {
    label: 'Timeline',
    category: 'Urgency',
    icon: Clock,
    color: 'blue',
    description:
      'Maps buying urgency and decision timeline for proper prioritization. Reduces sales cycle waste by 40%.',
  },
  {
    label: 'Authority',
    category: 'Decision',
    icon: Crown,
    color: 'amber',
    description:
      'Identifies decision-makers, influencers, and information-gatherers. Routes to right sales approach.',
  },
  {
    label: 'Need',
    category: 'Problem',
    icon: AlertCircle,
    color: 'rose',
    description:
      'Assesses pain point intensity and solution fit probability. Early mismatch detection saves time.',
  },
  {
    label: 'Fit',
    category: 'Solution',
    icon: CheckCircle,
    color: 'cyan',
    description:
      'Determines how well your offering matches prospect requirements. Identifies deal-breakers early.',
  },
  {
    label: 'Engage',
    category: 'Momentum',
    icon: MessageCircle,
    color: 'violet',
    description:
      'Tracks conversation quality and commitment level. 3x better prediction of conversion.',
  },
  {
    label: 'Emotion',
    category: 'Sentiment',
    icon: Heart,
    color: 'orange',
    description:
      'Detects emotional patterns and adapts tone in real-time. Context-aware responses feel human.',
  },
  {
    label: 'Context',
    category: 'Memory',
    icon: Database,
    color: 'teal',
    description:
      'Remembers returning visitors across sessions. 65% conversion on returning prospects vs. 28% first-visit.',
  },
  {
    label: 'Knowledge',
    category: 'Retrieval',
    icon: BookOpen,
    color: 'fuchsia',
    description:
      'Leverages vector databases for contextual information. Answers are grounded in retrieved source content, designed to reduce unsupported responses.',
  },
  {
    label: 'Integration',
    category: 'Connect',
    icon: Zap,
    color: 'indigo',
    description:
      'Seamless integration with CRMs, Slack, email workflows. Complete conversation history transfer.',
  },
  {
    label: 'Orchestrate',
    category: 'Agents',
    icon: Network,
    color: 'green',
    description:
      'Coordinates specialized agents while sharing intelligence. Sales, insurance, logistics—unified platform.',
  },
]

const colorMap: Record<string, { base: string; gradient: string[] }> = {
  emerald: { base: '#10b981', gradient: ['#34d399', '#059669', '#064e3b'] },
  blue: { base: '#3b82f6', gradient: ['#60a5fa', '#2563eb', '#1e3a8a'] },
  purple: { base: '#a855f7', gradient: ['#c084fc', '#9333ea', '#581c87'] },
  rose: { base: '#f43f5e', gradient: ['#fb7185', '#e11d48', '#881337'] },
  amber: { base: '#f59e0b', gradient: ['#fbbf24', '#d97706', '#78350f'] },
  cyan: { base: '#06b6d4', gradient: ['#22d3ee', '#0891b2', '#164e63'] },
  indigo: { base: '#6366f1', gradient: ['#818cf8', '#4f46e5', '#312e81'] },
  violet: { base: '#8b5cf6', gradient: ['#a78bfa', '#7c3aed', '#4c1d95'] },
  teal: { base: '#14b8a6', gradient: ['#2dd4bf', '#0d9488', '#115e59'] },
  orange: { base: '#f97316', gradient: ['#fb923c', '#ea580c', '#7c2d12'] },
  fuchsia: { base: '#d946ef', gradient: ['#e879f9', '#c026d3', '#701a75'] },
  green: { base: '#22c55e', gradient: ['#4ade80', '#16a34a', '#14532d'] },
}

interface OrbNodeProps {
  color: string
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>
  label: string
  category: string
  description: string
  isHovered: boolean
  onHover: () => void
  onLeave: () => void
  delay: number
  theme: 'light' | 'dark'
}

const benefitCards = [
  {
    title: 'Real 12-Dimension Scoring',
    description:
      'Not marketing fluff. Vector actually scores conversations across 12 qualification dimensions—and those scores directly determine routing. You see the math, not a black box.',
    icon: Grid3x3,
  },
  {
    title: 'Application You Own',
    description:
      'Not a SaaS subscription you rent. We build a system configured to your ICP, routing rules, CRM fields. 60-day deployment. You own the application code.',
    icon: Bot,
  },
]

const productionStats = [
  {
    label: 'Volume',
    metric: '50–200 Leads/Month',
    description: 'Right-sized for growth teams',
  },
  {
    label: 'Scoring',
    metric: '12 Scoring Dimensions',
    description: 'Real qualification, not theater',
  },
  {
    label: 'CRM',
    metric: 'Standard CRM Integration',
    description: 'HubSpot, Salesforce, Pipedrive',
  },
  {
    label: 'Build',
    metric: '60-Day Fixed Scope',
    description: 'You own the application code',
  },
]

export default function CenteredArc() {
  const [hoveredNode, setHoveredNode] = React.useState<number | null>(null)
  const [isMobile, setIsMobile] = React.useState(false)
  const [theme, setTheme] = React.useState<'light' | 'dark'>('dark')

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  React.useEffect(() => {
    const currentTheme = document.documentElement.getAttribute('data-theme') as
      | 'light'
      | 'dark'
    setTheme(currentTheme || 'dark')

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'data-theme') {
          const newTheme = document.documentElement.getAttribute(
            'data-theme'
          ) as 'light' | 'dark'
          setTheme(newTheme || 'dark')
        }
      })
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })

    return () => observer.disconnect()
  }, [])

  return (
    <>
      <style>{styles}</style>

      {/* FIX 1: Changed 'pt-12' to 'pt-0' for mobile.
          This removes the top padding completely on small screens.
          Desktop retains 'md:pt-24'.
      */}
      <div className='relative w-full min-h-screen bg-background flex flex-col items-center justify-start pt-0 md:pt-24 pb-16 md:pb-24 font-sans overflow-hidden'>
        <div className='absolute top-0 left-1/2 -translate-x-1/2 w-[150vw] h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-900/20 via-background to-background pointer-events-none' />

        <div className='relative flex flex-col items-center z-10 w-full'>
          {/* FIX 2: Increased mobile negative margin from -mt-[200px] to -mt-[220px].
             This pulls the orbit element even higher up off-screen on mobile.
          */}
          <div className='relative w-[1000px] flex-shrink-0 aspect-square flex items-center justify-center arc-mask-gradient overflow-visible -mt-[220px] md:-mt-[150px]'>
            <div className='absolute inset-0'>
              <div
                className='absolute inset-0'
                style={{
                  animation: 'orbitClockwise 60s linear infinite',
                  transformOrigin: '50% 60%',
                }}
              >
                {integrations.map((item, index) => {
                  const angle = index * 30
                  const radian = (angle * Math.PI) / 180

                  // Mobile: 28 (Breathing room) | Desktop: 36 (Wide)
                  const radius = isMobile ? 28 : 36
                  const centerX = 50
                  const centerY = 60

                  const x = centerX + radius * Math.cos(radian)
                  const y = centerY + radius * Math.sin(radian)
                  const delay = index * 100

                  return (
                    <div
                      key={item.label}
                      className='absolute w-20 h-20 md:w-24 md:h-24 -ml-10 -mt-10 md:-ml-12 md:-mt-12 flex items-center justify-center'
                      style={{
                        left: `${x}%`,
                        top: `${y}%`,
                      }}
                    >
                      <div className='counter-rotate-node w-full h-full flex items-center justify-center'>
                        <OrbNode
                          {...item}
                          isHovered={hoveredNode === index}
                          onHover={() => setHoveredNode(index)}
                          onLeave={() => setHoveredNode(null)}
                          delay={delay}
                          theme={theme}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className='absolute inset-0 flex flex-col items-center justify-center z-20 pointer-events-none pb-16 md:pb-24 gap-2 md:gap-3'>
              <span className='text-xs md:text-sm uppercase tracking-widest text-muted-foreground font-medium'>
                Introducing
              </span>
              <Image
                src={
                  theme === 'dark'
                    ? '/pixelmojo-vector-branding-dark.svg'
                    : '/pixelmojo-vector-branding-light.svg'
                }
                alt='Pixelmojo Vector'
                width={0}
                height={280}
                className='w-[160px] md:w-[240px] lg:w-[280px] h-auto drop-shadow-2xl'
                quality={100}
              />
            </div>
          </div>

          {/* Cards: Maintained strict negative margin.
              These carry the /vector redesign's `.fit-card` idiom — hairline
              grid cell, accent disc, display heading — instead of the old
              rounded translucent card. `.fit-cards` is a four-column grid in
              the source; there are two cards here, so the column count is the
              one value overridden. */}
          <div className='relative z-30 w-full -mt-[440px] md:-mt-[380px] px-4 md:px-12 max-w-5xl mx-auto'>
            <div className='vector-page'>
              <div
                className='fit-cards'
                style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}
              >
                {benefitCards.map((card, i) => (
                  <article
                    className='fit-card'
                    key={card.title}
                    style={
                      {
                        '--accent':
                          i === 0 ? 'var(--v-cyan-bright)' : 'var(--v-pink)',
                      } as React.CSSProperties
                    }
                  >
                    <div className='fit-icon'>
                      <card.icon className='w-4 h-4' />
                    </div>
                    <h3>{card.title}</h3>
                    <p>{card.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>

          {/* Production Stats — the redesign's section head plus its
              `.qualifier-cell` rail, the language that page already uses for a
              row of figures. `.qualifier-grid` leads with a wider intro column
              in the source; there is no intro cell here, so the columns are
              evened out. */}
          <div className='relative z-30 w-full px-4 md:px-12 max-w-5xl mx-auto mt-24 md:mt-32'>
            <div className='vector-page'>
              <div className='mb-10'>
                <p className='section-kicker'>Who Vector is built for</p>
                <h2 className='section-title'>Built for Teams Like Yours</h2>
                <p className='section-copy mt-5 max-w-2xl'>
                  Vector works best for growth-stage teams with steady inbound
                  volume, standard CRM setups, and a sales motion that rewards
                  faster qualification.
                </p>
              </div>
              <div
                className='qualifier-grid'
                style={{
                  width: '100%',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                }}
              >
                {productionStats.map(stat => (
                  <div className='qualifier-cell' key={stat.metric}>
                    <span className='v-rail-label'>{stat.label}</span>
                    <strong>{stat.metric}</strong>
                    <span>{stat.description}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function OrbNode({
  color,
  icon: Icon,
  label,
  category,
  description,
  isHovered,
  onHover,
  onLeave,
  delay,
  theme,
}: OrbNodeProps) {
  const c = colorMap[color]
  const isLight = theme === 'light'

  return (
    <div
      className='relative w-full h-full flex flex-col items-center justify-center cursor-pointer group'
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      style={{ pointerEvents: 'auto' }}
    >
      {/* Tooltip - Desktop only */}
      {isHovered && (
        <div className='hidden md:block absolute bottom-full mb-4 left-1/2 -translate-x-1/2 w-64 p-3 bg-card border border-border rounded-lg z-50 pointer-events-none'>
          <p className='text-xs text-foreground leading-relaxed'>
            {description}
          </p>
        </div>
      )}
      <div
        className='flex flex-col items-center justify-center transition-transform duration-300 group-hover:scale-110'
        style={{
          animation: 'fadeInUp 0.8s ease-out forwards',
          animationDelay: `${delay}ms`,
          opacity: 0,
        }}
      >
        {/* Glowing Orb */}
        <div className='relative w-12 h-12 md:w-14 md:h-14 mb-2 md:mb-3'>
          <div
            className='absolute inset-0 rounded-full blur-xl transition-opacity duration-300'
            style={{
              background: c.base,
              opacity: isLight
                ? isHovered
                  ? 0.8
                  : 0.6
                : isHovered
                  ? 0.7
                  : 0.4,
            }}
          />
          <div
            className='absolute inset-0 rounded-full shadow-inner flex items-center justify-center'
            style={{
              background: isLight
                ? `radial-gradient(circle at 30% 30%, ${c.gradient[0]}60, ${c.gradient[2]})`
                : `radial-gradient(circle at 30% 30%, ${c.gradient[0]}40, ${c.gradient[2]}90)`,
              border: isLight
                ? `2px solid ${c.base}40`
                : '1px solid rgba(255,255,255,0.2)',
              boxShadow: isHovered
                ? `0 0 24px ${c.base}${isLight ? '80' : '60'}, inset 0 0 12px ${isLight ? c.base + '30' : 'white'}`
                : `0 0 12px ${c.base}${isLight ? '60' : '30'}`,
            }}
          >
            <Icon
              className='w-5 h-5 md:w-6 md:h-6 text-white transition-all duration-300'
              style={{
                filter: isHovered ? 'drop-shadow(0 0 5px white)' : 'none',
                opacity: isHovered ? 1 : 0.9,
              }}
            />
          </div>
        </div>

        {/* Labels */}
        <div className='flex flex-col items-center space-y-0.5'>
          <span className='text-[9px] md:text-[10px] uppercase tracking-wider font-bold text-foreground whitespace-nowrap drop-shadow-md'>
            {label}
          </span>
          <span
            className='text-[8px] md:text-[9px] font-medium opacity-60 whitespace-nowrap transition-colors duration-300 drop-shadow-md text-muted-foreground'
            style={{ color: isHovered ? c.base : undefined }}
          >
            {category}
          </span>
        </div>
      </div>
    </div>
  )
}
